import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import swal from 'sweetalert2';
import Loading from '../Loading';
import { getExam, importAnswersAttachment } from '../../actions/examsActions';
import usePreventDoubleSubmit, { getSwalSubmitGuardOptions } from '../../customHooks/usePreventDoubleSubmit';

const styles = {
  main: {
    display: 'flex',
    minHeight: '70vh',
    flexFlow: 'column nowrap',
    justifyContent: 'space-between',
  },
  section: {
    padding: '20px',
  },
};

const MODES = {
  upsert: {
    label: 'Añadir / actualizar',
    swalType: 'question',
    steps: [
      'Creará respuestas con adjunto para estudiantes que aún no tienen respuesta.',
      'Si el estudiante no existe, se creará.',
      'Actualizará el PDF de respuestas existentes.',
      'Omitirá respuestas que ya tienen asignación de codificación.',
      'No eliminará respuestas existentes.',
    ],
  },
  replace: {
    label: 'Importar desde cero',
    swalType: 'warning',
    steps: [
      'Eliminará respuestas previas sin asignación',
      'Si el estudiante no existe, se creará.',
      'Subirá los PDFs y creará todas las respuestas del archivo.',
      'No modificará respuestas que ya tienen asignación de codificación.',
    ],
  },
};

const confirmImportMessage = (mode) => {
  const { label, steps } = MODES[mode];
  return `
    <div style="font-size:15px;text-align:left">
      <p><strong>Modo:</strong> ${label}</p>
      <p style="margin-top:12px"><strong>Esta acción:</strong></p>
      <ul style="padding-left:20px">
        ${steps.map((s) => `<li>${s}</li>`).join('')}
      </ul>
    </div>
  `;
};

const buildImportSummary = (data) => {
  const parts = [];
  if (data.deleted > 0) parts.push(`${data.deleted} eliminadas`);
  if (data.created > 0) parts.push(`${data.created} creadas`);
  if (data.updated > 0) parts.push(`${data.updated} actualizadas`);
  if (data.skipped > 0) parts.push(`${data.skipped} omitidas`);
  return parts.length > 0 ? parts.join(', ') : 'No se procesaron respuestas';
};

const ImportAnswersAttachment = (props) => {
  const examId = props.match.params.id;
  const { exam } = props;
  const zipInputRef = useRef(null);
  const excelInputRef = useRef(null);
  const [result, setResult] = useState(null);
  const [processingLocally, setProcessingLocally] = useState(false);
  const [mode, setMode] = useState('upsert');
  const { isSubmitting, guardSubmit, startSubmit, wrapCallbacks } = usePreventDoubleSubmit({ loadingText: 'Importando...' });

  const processingImport = exam?.processingImportAnswersAttachment;
  const isProcessing = processingImport || processingLocally;

  useEffect(() => {
    props.getExam(examId);
  }, [examId]);

  useEffect(() => {
    if (exam?.importAnswersAttachmentResult) {
      setResult(exam.importAnswersAttachmentResult);
    }
  }, [exam?.importAnswersAttachmentResult]);

  useEffect(() => {
    if (exam?.processingImportAnswersAttachment) {
      setProcessingLocally(true);
    } else if (exam?.importAnswersAttachmentResult) {
      setProcessingLocally(false);
    }
  }, [exam?.processingImportAnswersAttachment, exam?.importAnswersAttachmentResult]);

  useEffect(() => {
    if (processingImport) {
      swal({
        type: 'info',
        title: 'Importación en proceso',
        text: 'Hay una importación de adjuntos que aún se está procesando. Por favor recargue la página.',
        showConfirmButton: true,
        confirmButtonText: 'Entendido',
      });
    }
  }, [processingImport]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!guardSubmit()) return;

    if (isProcessing) {
      swal('Un momento', 'Ya hay una importación en proceso para este examen.', 'warning');
      return;
    }

    const zipFile = zipInputRef.current?.files[0];
    const excelFile = excelInputRef.current?.files[0];

    if (!zipFile || !excelFile) {
      swal('Un momento', 'Debe seleccionar el archivo ZIP y el archivo Excel', 'warning');
      return;
    }
    if (zipFile.name.split('.').pop() !== 'zip') {
      swal('Un momento', 'El archivo de adjuntos debe tener formato .zip', 'warning');
      return;
    }
    if (excelFile.name.split('.').pop() !== 'xlsx') {
      swal('Un momento', 'El archivo de respuestas debe tener formato .xlsx', 'warning');
      return;
    }

    swal({
      title: 'Confirmar importación',
      html: confirmImportMessage(mode),
      type: MODES[mode].swalType,
      width: '450px',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      ...getSwalSubmitGuardOptions('Importando...'),
    }).then((confirm) => {
      if (!confirm.value || !guardSubmit()) return;

      const formData = new FormData();
      formData.append('answers_list', excelFile);
      formData.append('answers_zip', zipFile);
      formData.append('mode', mode);

      startSubmit();
      setResult(null);

      const { successCallback, errorCallback } = wrapCallbacks(
        (data) => {
          setProcessingLocally(true);
          swal(
            'Importación realizada',
            data.message || 'la importación de los adjuntos se esta procesando. Por favor recargue la página para verificar el estado.',
            'success'
          );
        },
        (error) => {
          const responseData = error?.response?.data;
          const validationErrors = responseData?.errors;

          if (validationErrors && validationErrors.length > 0) {
            setResult({ created: 0, updated: 0, deleted: 0, skipped: 0, errors: validationErrors });
            swal({
              type: 'error',
              title: 'Error al importar adjuntos',
              text: `Se encontraron ${validationErrors.length} errores.`,
            });
            return;
          }

          const msg = responseData?.error || 'Error al importar los adjuntos';
          swal('Error', msg, 'error');
        }
      );

      props.importAnswersAttachment(examId, formData, successCallback, errorCallback);
    });
  };

  if (!exam?.tests) return <Loading />;

  const formDisabled = isSubmitting || isProcessing;

  return (
    <main style={styles.main}>
      <form onSubmit={handleSubmit}>
        <section style={styles.section}>
          <h2>Importar respuestas con adjuntos</h2>
          <p>
            A continuación, podrá importar respuestas de estudiantes subiendo un archivo Excel (.xlsx)
            y un archivo ZIP (.zip) con los adjuntos. El Excel debe contener las columnas{' '}
            <strong>Email</strong>, <strong>Nombre</strong>, <strong>test_id</strong> e{' '}
            <strong>Identificador</strong>.
          </p>
          <p>
            La columna <strong>test_id</strong> corresponde al id de la pregunta asociada al examen.
            La columna <strong>Identificador</strong> debe coincidir con el nombre de la carpeta
            dentro del ZIP.
          </p>

          <p>Preguntas de este examen y sus <strong>test_id</strong>:</p>
          <table className="table">
            <thead>
              <tr>
                <td><strong>Pregunta</strong></td>
                <td><strong>test_id</strong></td>
              </tr>
            </thead>
            <tbody>
              {exam.tests.map((test) => (
                <tr key={test.id}>
                  <td>{test.title}</td>
                  <td>{test.id}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p>Ejemplo del Excel:</p>
          <table className="table">
            <thead>
              <tr>
                <td><strong>Email</strong></td>
                <td><strong>Nombre</strong></td>
                <td><strong>Test_id</strong></td>
                <td><strong>Identificador</strong></td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>usuario1@correo.edu.co</td>
                <td>pdf_usuario1.pdf</td>
                <td>39</td>
                <td>IdentificadorUnico</td>
              </tr>
              <tr>
                <td>usuario2@correo.edu.co</td>
                <td>pdf_usuario2.pdf</td>
                <td>39</td>
                <td>IdentificadorUnico</td>
              </tr>
            </tbody>
          </table>

          <p>
            El nombre de cada archivo dentro del ZIP debe coincidir exactamente con la columna{' '}
            <strong>Nombre</strong> del Excel, y debe estar dentro de la carpeta cuyo nombre debe coincidir exactamente
            con el valor de la columna <strong>Identificador</strong>.
          </p>

          <div className="row">
            <div className="col-md-4 col-xs-12">
              <label>Tipo de importación</label>
              <select
                className="form-control"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                disabled={formDisabled}
              >
                {Object.entries(MODES).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4 col-xs-12">
              <label>ZIP con adjuntos</label>
              <br />
              <input ref={zipInputRef} type="file" accept=".zip" required disabled={formDisabled} />
            </div>
            <div className="col-md-4 col-xs-12">
              <label>Excel de respuestas (.xlsx)</label>
              <br />
              <input ref={excelInputRef} type="file" accept=".xlsx" required disabled={formDisabled} />
            </div>
          </div>

          {result && (
            <div style={{ marginTop: '16px' }}>
              {result.errors && result.errors.length > 0 ? (
                <div>
                  <p className="alert alert-danger">
                    <strong>Error al importar adjuntos:</strong>
                  </p>
                  <ul className="text-danger">
                    {result.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="alert alert-success">
                  <p><strong>Resumen:</strong> {buildImportSummary(result)}</p>
                </div>
              )}
            </div>
          )}
          {isProcessing && (
            <div style={{ marginTop: '16px' }}>
              <div className="alert alert-info">
                Hay una importación en proceso. Por favor recargue la página para verificar el estado.
              </div>
            </div>
          )}

        </section>

        <section
          style={{
            display: 'flex',
            alignItems: 'center',
            flexFlow: 'row nowrap',
            justifyContent: 'space-between',
            margin: '2rem',
          }}
        >
          <button
            type="button"
            onClick={() => props.history.push(`/exams/${examId}/editParticipants`)}
            className="btn btn-danger"
          >
            Volver
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={formDisabled}
          >
            {isProcessing ? 'Importando...' : 'Importar adjuntos'}
          </button>
        </section>
      </form>
    </main>
  );
};

function mapStateToProps(state, ownProps) {
  return {
    exam: state.exams[ownProps.match.params.id],
  };
}

export default connect(mapStateToProps, { getExam, importAnswersAttachment })(ImportAnswersAttachment);
